const todayStr = () => new Date().toISOString().slice(0, 10);

export function initialTransitionForm() {
  return {
    date: todayStr(),
    price: '',
    person_name: '',
    person_contact: '',
    received_description: '',
    loss_reason: '',
    expected_return_at: '',
    notes: '',
  };
}

const trim = (v) => (typeof v === 'string' ? v.trim() || null : v || null);

export function buildSalePayload(plantId, form) {
  return {
    plant_id: plantId,
    date: form.date || null,
    price: form.price ? parseFloat(form.price) : null,
    buyer_name: trim(form.person_name),
    buyer_contact: trim(form.person_contact),
    notes: trim(form.notes),
  };
}

export function buildDispositionPayload(plantId, targetStatus, form) {
  return {
    plant_id: plantId,
    type: targetStatus,
    date: form.date || null,
    counterparty_name: trim(form.person_name),
    counterparty_contact: trim(form.person_contact),
    received_description: targetStatus === 'swapped' ? trim(form.received_description) : null,
    loss_reason: targetStatus === 'deceased' ? (form.loss_reason || null) : null,
    notes: trim(form.notes),
    // set_status: true is the default — endpoint sets plant status atomically
  };
}

export function buildLoanPayload(plantId, targetStatus, form) {
  return {
    plant_id: plantId,
    direction: targetStatus,
    person_name: trim(form.person_name),
    person_contact: trim(form.person_contact),
    loaned_at: form.date || null,
    expected_return_at: form.expected_return_at || null,
    notes: trim(form.notes),
    // set_status: true is the default — endpoint sets plant status atomically
  };
}

/**
 * Save a lifecycle status transition for a plant.
 * Each endpoint atomically creates the domain record AND sets the plant status,
 * so no prior PUT /plants/{id} is needed.
 *
 * Returns the created domain record from the server (sale, disposition, or loan row).
 * Current callers (StatusTransitionSheet) ignore the return value and rely on
 * fetchData() to refresh state, but the record is available for:
 *   - Smarter toasts that reference the created ID or record details
 *   - Immediate follow-up actions (e.g. open the new disposition for editing)
 *   - PlantForm migration: when createStatusRecord() is refactored to call this
 *     helper, PlantForm can confirm the ID and wire up inline edit flows without
 *     a second fetch
 */
export async function saveStatusTransition(plantId, targetStatus, form) {
  if (targetStatus === 'sold') {
    const r = await fetch('/sales', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildSalePayload(plantId, form)),
    });
    if (!r.ok) throw new Error((await r.json()).detail || 'Failed to record sale');
    return r.json();
  }

  if (['gifted', 'swapped', 'deceased'].includes(targetStatus)) {
    const r = await fetch('/plant-dispositions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildDispositionPayload(plantId, targetStatus, form)),
    });
    if (!r.ok) throw new Error((await r.json()).detail || 'Failed to record disposition');
    return r.json();
  }

  if (['loaned_in', 'loaned_out'].includes(targetStatus)) {
    const r = await fetch('/plant-loans', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildLoanPayload(plantId, targetStatus, form)),
    });
    if (!r.ok) throw new Error((await r.json()).detail || 'Failed to record loan');
    return r.json();
  }

  throw new Error(`No transition handler for status: ${targetStatus}`);
}
